import { currentToken, getAccessToken, invalidateToken, onTokenChange } from '../auth/gis';

/**
  * Page half of the service-worker conversation.
  *
  * The worker does the streaming but never authenticates: it asks here for a
  * token, and this module answers using the same GIS session the rest of the app
  * uses. Keeping sign-in on the page side means there is exactly one place that
  * can talk to Google Identity Services.
  */

export const DRIVE_STREAM_PREFIX = '/__drive/';

let registration: ServiceWorkerRegistration | null = null;
let bridged = false;
let registrationError: string | null = null;

/** Why the worker is unavailable, if it is. Surfaced in Settings rather than
  * left as a silent console warning, because it changes how Drive streams. */
export function serviceWorkerError(): string | null {
   return registrationError;
}

export function serviceWorkerSupported(): boolean {
   return 'serviceWorker' in navigator && window.isSecureContext;
}

/** Registers the worker and wires up the token channel. Safe to call twice. */
export async function ensureServiceWorker(): Promise<ServiceWorkerRegistration | null> {
   if (!serviceWorkerSupported()) return null;
   if (registration) return registration;

   try {
     // `import.meta.env.BASE_URL` keeps this correct when the app is hosted
     // under a subpath, e.g. GitHub Pages.
     const swUrl = `${import.meta.env.BASE_URL}sw.js`;
     registration = await navigator.serviceWorker.register(swUrl, {
       scope: import.meta.env.BASE_URL,
     });
     await navigator.serviceWorker.ready;
   } catch (err) {
     registrationError = err instanceof Error ? err.message : String(err);
     console.warn('[shelf] service worker registration failed', err);
     return null;
   }

   installBridge();
   pushToken(currentToken());
   return registration;
}

function installBridge(): void {
   if (bridged) return;
   bridged = true;

   navigator.serviceWorker.addEventListener('message', (event) => {
     const data = event.data as { type?: string; force?: boolean } | undefined;
     if (data?.type !== 'shelf:need-token') return;

     const port = event.ports[0];
     if (!port) return;

     // `force` means the token we last handed over was rejected mid-stream.
     if (data.force) invalidateToken();

     getAccessToken({ interactive: false })
       .then((token) => port.postMessage({ token }))
       .catch(() => port.postMessage({ token: null }));
   });

   onTokenChange(pushToken);
}

function pushToken(token: string | null): void {
   navigator.serviceWorker?.controller?.postMessage({ type: 'shelf:token', token });
   registration?.active?.postMessage({ type: 'shelf:token', token });
}

/** The same-origin URL an <audio> element can play for a given Drive file. */
export function driveStreamUrl(fileId: string): string {
   return `${import.meta.env.BASE_URL}__drive/${encodeURIComponent(fileId)}`;
}

/** True once a worker is actually controlling this page. Until then, Drive
  * playback would fall through to the network and 404. */
export function isControlled(): boolean {
   return Boolean(navigator.serviceWorker?.controller);
}
